import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle2, User, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
const testimonials = [{
  quote: "I uploaded six months of texts and the report quietly named three patterns I'd been calling 'just stress.' Uncomfortable, but useful.",
  author: 'Sarah J.',
  role: 'Product designer',
  rating: 5
}, {
  quote: "The Message Rewriter is what I use most days. It helps me say what I mean — without the edge I didn't realize was there.",
  author: 'Marcus D.',
  role: 'Software engineer',
  rating: 5
}, {
  quote: 'As a therapist, I find the reports a useful conversation starter with clients who want to look at their own communication style.',
  author: 'Em W., LMFT',
  role: 'Licensed therapist',
  rating: 5
}, {
  quote: 'Reading the breakdown of a DARVO exchange with my ex was clarifying. Not dramatic — just clear, with real evidence.',
  author: 'Alex R.',
  role: 'Grad student',
  rating: 5
}, {
  quote: "Privacy was my one requirement. Knowing scans aren't tied to my identity is what made me actually willing to upload anything.",
  author: 'Jenna B.',
  role: 'Founder',
  rating: 4
}, {
  quote: "It didn't tell me my friendship was 'toxic.' It told me which specific patterns were repeating and let me decide what to do.",
  author: 'Chris M.',
  role: 'Teacher',
  rating: 5
}];
export function SocialProofSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden border-t border-gray-900" data-id="element-2837" data-mp="prgff7emjpj6">
      {/* Subtle Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30 pointer-events-none" data-id="element-2838" data-mp="zmnimyhssqax" />

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-2839" data-mp="qlfomsmbebmn">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20" data-id="element-2840" data-mp="ln4d8dgdft2b">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-3 bg-[#0A0A0A] border border-gray-800 rounded-full px-5 py-2.5 mb-8 shadow-[0_0_30px_rgba(0,0,0,0.5)]" data-id="element-2841" data-mp="got4k61tbolo">
            <div className="flex gap-0.5" data-id="element-2842" data-mp="3p4miqe8o8nzk">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} size={14} className={s === 5 ? 'text-[#FFD700]/50' : 'text-[#FFD700] fill-[#FFD700]'} data-id="element-2843" data-mp="3p6z28vu2v6eq" />)}
            </div>
            <div className="h-3 w-px bg-gray-800" data-id="element-2844" data-mp="3txyj6f8kbzb0" />
            <span className="text-gray-300 font-mono text-[11px] tracking-[0.2em] uppercase font-bold" data-id="element-2845" data-mp="3twnjaqi8nbp6">
              <span className="text-white" data-id="element-2846" data-mp="2wbrfr8nzwd08">4.9/5</span> · 10,000+ Scans Analyzed
            </span>
            <div className="h-3 w-px bg-gray-800" data-id="element-2847" data-mp="319syfshieab6" />
            <ShieldCheck size={14} className="text-[#FF0000]" data-id="element-2848" data-mp="368nq7jxgf97f" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl md:text-5xl font-black text-white tracking-tight mb-5 uppercase" data-id="element-2849" data-mp="3az441556swub">
            Field Reports from{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF0000] to-red-500" data-id="element-2850" data-mp="xseax4ff3eo5">
              The Front Lines
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-gray-400 font-mono text-sm md:text-base max-w-2xl mx-auto leading-relaxed" data-id="element-2851" data-mp="szl4mc4avy9y">
            Notes from people using ToxicDetector to read their own
            conversations more clearly.
          </motion.p>
        </div>

        {/* Carousel Controls */}
        <div className="flex justify-end gap-3 mb-6" data-id="element-2852" data-mp="1t0yefwarq9id">
          <button onClick={() => scroll('left')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FF0000] transition-colors" aria-label="Previous testimonials" data-id="element-2853" data-mp="1t3kukkbq5xc7">
            <ChevronLeft size={20} data-id="element-2854" data-mp="1o8ey4rkp509v" />
          </button>
          <button onClick={() => scroll('right')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FF0000] transition-colors" aria-label="Next testimonials" data-id="element-2855" data-mp="1a9ywhf1oxzyl">
            <ChevronRight size={20} data-id="element-2856" data-mp="15covr38uq1vg" />
          </button>
        </div>

        {/* Testimonials Carousel */}
        <div ref={scrollContainerRef} className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory hide-scrollbar" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-2857" data-mp="2urd5sxtyz7qb">
          {testimonials.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className="relative bg-[#0A0A0A] border border-gray-800 rounded-xl p-7 flex flex-col group hover:border-[#FF0000]/40 transition-colors duration-300 min-w-[320px] md:min-w-[400px] snap-start flex-shrink-0" data-id="element-2858" data-mp="2t8d707uojp55" data-mpi={index}>
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-tl-lg" data-id="element-2859" data-mp="3m72uxb1jixs" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-tr-lg" data-id="element-2860" data-mp="1hhp8il1pm6ig" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-bl-lg" data-id="element-2861" data-mp="1qhc0kg8jtzyu" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-700 group-hover:border-[#FF0000] transition-colors rounded-br-lg" data-id="element-2862" data-mp="1vhghg7ddcwpl" />

              {/* Header row */}
              <div className="flex items-center justify-between mb-5" data-id="element-2863" data-mp="25cib9gilamyi">
                <div className="flex gap-0.5" data-id="element-2864" data-mp="2a64fckaotiip">
                  {[...Array(5)].map((_, i) => <Star key={i} size={13} className={i < item.rating ? 'text-[#FFD700] fill-[#FFD700]' : 'text-gray-800'} data-id="element-2865" data-mp="2e5ll7eu10hoe" data-mpi={i} />)}
                </div>
                <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded" data-id="element-2866" data-mp="1u2kqgc0mnipp">
                  <CheckCircle2 size={10} className="text-emerald-400" data-id="element-2867" data-mp="282n2viq61jxk" />
                  <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-[0.15em]" data-id="element-2868" data-mp="240s1ke88j6js">
                    Verified
                  </span>
                </div>
              </div>

              {/* Quote */}
              <p className="text-gray-300 leading-relaxed text-sm md:text-[15px] flex-grow mb-6" data-id="element-2869" data-mp="1bcvxupk90edi">
                "{item.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 border-t border-gray-800/80 pt-4 mt-auto" data-id="element-2870" data-mp="1kd158zpfzcro">
                <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-2871" data-mp="wmygasgtqmxa">
                  <User size={15} className="text-gray-500" data-id="element-2872" data-mp="sktolz9xs0da" />
                </div>
                <div className="min-w-0" data-id="element-2873" data-mp="ihek7bhdx7rq">
                  <p className="text-white font-semibold text-sm truncate" data-id="element-2874" data-mp="igd6t0cwc96u">
                    {item.author}
                  </p>
                  <p className="text-gray-500 text-[11px] mt-0.5 tracking-wide" data-id="element-2875" data-mp="8jqpumnoy2a3">
                    {item.role}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
      __html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `
    }} data-id="element-2876" data-mp="1197cf3omqoye" />
    </section>;
}